/**
 * Pure geometry behind the full-screen card viewer (#327): fit, zoom bounds,
 * and focal zoom. Kept out of the component so the math that decides whether
 * a member ID is legible at 100% is pinned by tests, not by dragging a mouse.
 *
 * Coordinate system: the image centre starts at the stage centre; `tx`/`ty`
 * are the screen-pixel offset of the image centre from the stage centre, and
 * `zoom` is relative to FIT — zoom 1 means the whole card is visible, the
 * same framing the pane's thumbnail gives. Native resolution is a DERIVED
 * point in that system (`nativeZoom`), not the unit — which is why the
 * bounds have to make room for it explicitly.
 */

export interface Size { w: number; h: number }

export interface ViewTransform {
  /** Multiplier over the fit scale — 1 = the whole image visible. */
  zoom: number
  tx: number
  ty: number
}

/** The zoom floor and ceiling for one (image, stage, rotation) combination. */
export interface ZoomBounds { min: number; max: number }

export const VIEW_RESET: ViewTransform = { zoom: 1, tx: 0, ty: 0 }

/** Button/keyboard zoom step. */
export const ZOOM_STEP = 1.5

/** The image's bounding box after a 90°-multiple rotation — 90/270 swap axes. */
export function rotatedSize(image: Size, deg: number): Size {
  return deg % 180 === 0 ? image : { w: image.h, h: image.w }
}

/**
 * Scale that fits the (rotated) image inside the stage — the viewer's zoom-1.
 * Unmeasurable sizes (image not loaded, stage collapsed) fall back to 1 so the
 * viewer degrades to rendering at natural size rather than dividing by zero.
 */
export function fitScale(stage: Size, image: Size, deg: number): number {
  const r = rotatedSize(image, deg)
  if (r.w <= 0 || r.h <= 0 || stage.w <= 0 || stage.h <= 0) return 1
  return Math.min(stage.w / r.w, stage.h / r.h)
}

/**
 * The zoom that renders the photo at its own pixels — the "100%" the hint
 * under the stage promises, expressed in fit-relative units.
 */
export function nativeZoom(fit: number): number {
  return fit > 0 ? 1 / fit : 1
}

/**
 * The zoom ceiling: 8× fit for a small photo, and always enough to reach at
 * least 200% of NATIVE resolution on a large one — "zoom to native resolution
 * or beyond" is the ticket's floor, and a phone photo of a card is routinely
 * far larger than the stage that fits it.
 */
export function maxZoom(fit: number): number {
  return Math.max(8, fit > 0 ? 2 / fit : 8)
}

/**
 * The zoom floor. Fit is usually the widest view there is, but a photo the
 * stage has to UPSCALE (fit > 1 — a small MMS card on a big screen) puts
 * native resolution BELOW fit, and a floor at fit made 100% unreachable on
 * exactly those images while the hint kept promising it. The floor is the
 * wider of the two views and never anything lower: zooming out still stops at
 * something meaningful instead of shrinking the card inside an empty stage.
 */
export function minZoom(fit: number): number {
  return Math.min(1, nativeZoom(fit))
}

export function zoomBounds(fit: number): ZoomBounds {
  return { min: minZoom(fit), max: maxZoom(fit) }
}

export function clampZoom(zoom: number, bounds: ZoomBounds): number {
  return Math.min(Math.max(zoom, bounds.min), bounds.max)
}

/**
 * Zoom keeping the stage point (px, py — offsets from the stage centre) fixed:
 * the member ID being zoomed AT stays under the cursor instead of sliding
 * off-screen. At or below fit the whole card is on screen and there is nowhere
 * to pan to, so those zooms recentre — "fit" always means the whole card,
 * never a fit-sized image parked in a corner.
 */
export function focalZoom(
  view: ViewTransform, nextZoomRaw: number, bounds: ZoomBounds, px: number, py: number,
): ViewTransform {
  const zoom = clampZoom(nextZoomRaw, bounds)
  if (zoom <= 1) return { ...VIEW_RESET, zoom }
  const k = zoom / view.zoom
  return { zoom, tx: px + (view.tx - px) * k, ty: py + (view.ty - py) * k }
}
